import {
  CountResult,
  Entity,
  EntityModel,
  EntityOperation,
  FieldValue,
  FormModel,
  GenericFieldValue,
  LinkDefinition,
  LookupFieldDefinition,
  LookupValue,
  PagedList,
  readFullModel,
  ServerOperation,
  ViewModel,
} from '@msdk/definitions'

import { base64encode, mapEntity, mapFilesOnMultipartForm, mapViewRow } from '../../utils'
import { ApiClient } from '../core'
import { ApiError } from '../core/errors'
import { handleError } from './error-handler'
import { NotFoundError } from './not-found-error'
import { GetViewInput } from './types'

export type EntityAPI = {
  createEntity(
    msProvider: string,
    entity: EntityModel,
    parentId?: string,
    includeAcl?: boolean,
    formModel?: FormModel,
    prefilledFIelds?: Record<string, GenericFieldValue>,
  ): Promise<EntityOperation>
  getEntity(
    msProvider: string,
    entityId: string,
    entity: EntityModel,
    includeAcl?: boolean,
    formModel?: FormModel,
  ): Promise<EntityOperation>
  getEntities(msProvider: string, entityIds: string[], entityModel: EntityModel): Promise<EntityOperation>
  getSingleton(
    msProvider: string,
    parentId: string,
    entity: EntityModel,
    formModel?: FormModel,
  ): Promise<EntityOperation>
  saveEntities(
    msProvider: string,
    entity: EntityModel,
    entities: Entity[],
    formModel?: FormModel,
    actionId?: string,
  ): Promise<EntityOperation>
  forceSaveEntities(
    msProvider: string,
    entity: EntityModel,
    entities: Entity[],
    formModel?: FormModel,
    actionId?: string,
  ): Promise<EntityOperation>
  getView(msProvider: string, entity: EntityModel, viewName: string, opts?: GetViewInput): Promise<PagedList>
  getViewCount(msProvider: string, entityName: string, viewName: string): Promise<CountResult>
  getLookup(
    msProvider: string,
    entityName: string,
    id: string,
    lookupDefinition: LookupFieldDefinition,
    link: LinkDefinition,
  ): Promise<LookupValue>
  getAttachmentByUrl(url: string): Promise<Blob>
  createEntityWithPredefinedData(
    msProvider: string,
    entity: EntityModel,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, FieldValue<any>>,
    includeAcl?: boolean,
  ): Promise<EntityOperation>
  deleteEntity(msProvider: string, entityName: string, id: string, formName?: string): Promise<EntityOperation>
  deleteEntities(msProvider: string, entities: Record<string, string[]>): Promise<ServerOperation>
  cloneEntity(msProvider: string, entityName: string, id: string, save?: boolean): Promise<Entity>
}

export function initializeEntityAPI(c: ApiClient): EntityAPI {
  /**
   * This is an asynchronous function that retrieves an entity from a specified microservice provider and
   * maps it to a specified entity model.
   * @param {string} entityId - A string representing the unique identifier of the entity being
   * requested.
   * @param {EntityModel} entity - The `entity` parameter is an object of type `EntityModel` which
   * contains information about the entity being retrieved, such as its name and properties.
   * @param {string} msProvider - The `msProvider` parameter is a string that represents the microservice
   * provider from which the entity data will be retrieved. It is used to construct the URL for the API
   * call to retrieve the entity data.
   * @returns The function `getEntity` is returning a Promise that resolves to an `EntityOperation`
   * object.
   */
  const getEntity = async (
    msProvider: string,
    entityId: string,
    entity: EntityModel,
    includeAcl?: boolean,
    formModel?: FormModel,
  ): Promise<EntityOperation> => {
    const resultEntities: Entity[] = []
    // resolve MS
    // load data
    const res = await c.read<EntityOperation>(
      `${msProvider}/api/entity/${entity.name}/${entityId}?includeAcl=${includeAcl}&formName=${formModel?.name}`,
    )
    if (res instanceof ApiError) {
      if (res.status === 404) {
        throw new NotFoundError(JSON.stringify(res))
      }
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      // map entity
      res.entities.forEach((e) => {
        resultEntities.push(mapEntity(e, entity, formModel))
      })
      res.entities = resultEntities
      return res
    }
  }

  /**
   * This is an asynchronous function that retrieves all entities matching the given ids,
   * from a specified microservice provider.
   * @param {string} msProvider - The `msProvider` parameter is a string that represents the provider of
   * the microservice. It is used to construct the API endpoint URL for fetching data.
   * @param {string} entityIds - The `entityIds` parameter is a string array that represents the list of desired
   * entities.
   * @param {EntityModel} entityModel - The `entityModel` parameter is of type `EntityModel` and represents the
   * entity for which the singleton is being retrieved. It contains information about the entity such as
   * its name, properties, and relationships.
   * @returns The function `getEntities` is returning a Promise that resolves to an `EntityOperation`
   * object.
   */
  const getEntities = async (
    msProvider: string,
    entityIds: string[],
    entityModel: EntityModel,
  ): Promise<EntityOperation> => {
    const rawEntities: Entity[] = []
    const pageSize = 30

    for (let i = 0; i < entityIds.length; i += pageSize) {
      const pageIds = entityIds.slice(i, i + pageSize)
      const res = await c.post<PagedList, any>(`${msProvider}/api/entity/GetAll/${entityModel.name}`, pageIds)

      if (res instanceof ApiError) {
        if (res.status === 404) {
          throw new NotFoundError(JSON.stringify(res))
        }
        throw new Error(`💥 ${JSON.stringify(res)}`)
      }

      if (res.source?.length) {
        rawEntities.push(...res.source)
      }
    }
    return { entities: rawEntities.map((e) => mapEntity(e, entityModel)) ?? [] } as EntityOperation
  }

  /**
   * The function `getSingleton` retrieves a singleton entity from a microservice, maps it to a specific
   * model, and returns the result.
   * @param {string} msProvider - The `msProvider` parameter is a string that represents the provider of
   * the microservice. It is used to construct the API endpoint URL for fetching data.
   * @param {string} parentId - The `parentId` parameter is a string that represents the ID of the parent
   * entity. It is used to retrieve the singleton entity associated with the specified parent entity.
   * @param {EntityModel} entity - The `entity` parameter is of type `EntityModel` and represents the
   * entity for which the singleton is being retrieved. It contains information about the entity such as
   * its name, properties, and relationships.
   * @param {FormModel} [formModel] - The `formModel` parameter is an optional parameter of type
   * `FormModel`. It represents the form model that is used to retrieve the entity. If provided, it is
   * used to include the form name in the API request URL. If not provided, the API request URL does not
   * include the form name
   * @returns The function `getSingleton` returns a Promise that resolves to an `EntityOperation` object.
   */
  const getSingleton = async (
    msProvider: string,
    parentId: string,
    entity: EntityModel,
    formModel?: FormModel,
  ): Promise<EntityOperation> => {
    const resultEntities: Entity[] = []
    // resolve MS
    // load data
    const res = await c.read<EntityOperation>(
      `${msProvider}/api/entity/${entity.name}?parentId=${parentId}&createIfNotExists=true&formName=${formModel?.name}`,
    )
    if (res instanceof ApiError) {
      if (res.status === 404) {
        throw new NotFoundError(JSON.stringify(res))
      }
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      // map entity
      res.entities.forEach((e) => {
        resultEntities.push(mapEntity(e, entity, formModel))
      })
      res.entities = resultEntities
      return res
    }
  }

  /**
   * This function creates an entity by sending a request to a microservice and mapping the response to a
   * specified entity model.
   * @param {string} msProvider - A string representing the microservice provider for the entity being
   * created.
   * @param {EntityModel} entity - The `entity` parameter is an object of type `EntityModel` which
   * contains information about the entity being created, such as its name and properties.
   * @returns The function `createEntity` is returning a Promise that resolves to an `EntityOperation`
   * object.
   */
  const createEntity = async (
    msProvider: string,
    entity: EntityModel,
    parentId?: string,
    includeAcl?: boolean,
    formModel?: FormModel,
    prefilledFIelds?: Record<string, GenericFieldValue>,
  ): Promise<EntityOperation> => {
    const resultEntities: Entity[] = []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = await c.create<EntityOperation, any>(
      `${msProvider}/api/entity/${entity.name}/create${parentId ? `/${parentId}` : ''}?includeAcl=${includeAcl}${
        formModel?.name ? `&formName=${formModel?.name}` : ''
      }`,
      prefilledFIelds ?? {},
    )
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      // map entity
      res.entities.forEach((e) => {
        resultEntities.push(mapEntity(e, entity, formModel))
      })
      res.entities = resultEntities
      return res
    }
  }

  /**
   * This function creates an entity by sending a request to a microservice and mapping the response to a
   * specified entity model.
   * @param {string} msProvider - A string representing the microservice provider for the entity being
   * created.
   * @param {EntityModel} entity - The `entity` parameter is an object of type `EntityModel` which
   * contains information about the entity being created, such as its name and properties.
   * @param {Record<string, FieldValue<any>>} data - The fields to prefill
   * created.
   * @returns The function `createEntity` is returning a Promise that resolves to an `EntityOperation`
   * object.
   */
  const createEntityWithPredefinedData = async (
    msProvider: string,
    entity: EntityModel,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, FieldValue<any>>,
    includeAcl?: boolean,
  ): Promise<EntityOperation> => {
    const resultEntities: Entity[] = []
    // resolve MS
    // load data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = await c.create<EntityOperation, any>(
      `${msProvider}/api/entity/${entity.name}/create?includeAcl=${includeAcl}`,
      data,
    )
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      // map entity
      res.entities.forEach((e) => {
        resultEntities.push(mapEntity(e, entity))
      })
      res.entities = resultEntities
      return res
    }
  }

  const internalSaveEntities = async (
    msProvider: string,
    entity: EntityModel,
    entities: Entity[],
    formModel?: FormModel,
    actionId?: string,
    force = false,
  ): Promise<EntityOperation> => {
    const id = entities.find((e) => e.entityName === entity.name)?.id
    if (id === undefined) {
      throw new Error('⚠️ Cannot find entity !')
    }
    const resultEntities: Entity[] = []
    const data: FormData = new FormData()
    try {
      data.append('@Entities', JSON.stringify(entities))
      mapFilesOnMultipartForm(entities, entity, data)
    } catch (error) {
      throw new Error(`💥 Could not serialize entity: ${(error as Error).message}`) // when needed
    }

    const qs = new URLSearchParams()
    if (formModel?.name) qs.append('formName', formModel.name)
    if (actionId) qs.append('actionId', actionId)

    const route = force ? 'entity/force' : 'entity'

    const res = await c.updateFormData<EntityOperation>(
      `${msProvider}/api/${route}/${entity.name}/${id}?${qs.toString()}`,
      data,
    )
    if (res instanceof ApiError) {
      /**
       * try to handle the error, and look for messages to map to the form
       * if the error can not be handled this way just throw the exception
       */
      const result = handleError(res, id)
      if (result) {
        return result
      } else {
        throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
      }
    } else {
      // map entity
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const entities = res.entities ?? []
      entities.forEach((e) => {
        resultEntities.push(mapEntity(e, entity, formModel))
      })
      res.entities = resultEntities
      return res
    }
  }

  /**
   * This is a TypeScript function that saves entities by forcing an update using an API endpoint and returns
   * the updated entities.
   * @param {string} msProvider - A string representing the microservice provider.
   * @param {EntityModel} entity - The `entity` parameter is an object of type `EntityModel` which
   * represents the metadata of an entity. It contains information such as the name of the entity, its
   * properties, and relationships with other entities.
   * @param {Entity[]} entities - An array of Entity objects.
   * @returns The function `forceSaveEntities` returns a `Promise` that resolves to an `EntityOperation`
   * object.
   */
  const forceSaveEntities = async (
    msProvider: string,
    entity: EntityModel,
    entities: Entity[],
    formModel?: FormModel,
    actionId?: string,
  ): Promise<EntityOperation> => {
    return internalSaveEntities(msProvider, entity, entities, formModel, actionId, true)
  }

  /**
   * This is a TypeScript function that saves entities by updating them in an API endpoint and returns
   * the updated entities.
   * @param {string} msProvider - A string representing the microservice provider.
   * @param {EntityModel} entity - The `entity` parameter is an object of type `EntityModel` which
   * represents the metadata of an entity. It contains information such as the name of the entity, its
   * properties, and relationships with other entities.
   * @param {Entity[]} entities - An array of Entity objects.
   * @returns The function `saveEntities` returns a `Promise` that resolves to an `EntityOperation`
   * object.
   */
  const saveEntities = async (
    msProvider: string,
    entity: EntityModel,
    entities: Entity[],
    formModel?: FormModel,
    actionId?: string,
  ): Promise<EntityOperation> => {
    return internalSaveEntities(msProvider, entity, entities, formModel, actionId, false)
  }

  /**
   * This is a TypeScript function that fetches a view of entities from a specified microservice provider
   * and maps the result to a PagedList object.
   * @param {string} msProvider - `msProvider` is a string parameter that represents the name of the
   * microservice provider.
   * @param {EntityModel} entity - `entity` is an object that represents a specific entity model. It
   * contains information about the entity's name, properties, and relationships with other entities. The
   * `getView` function uses this information to construct a query to fetch data from the API.
   * @param {string} viewName - The name of the view that needs to be fetched from the API.
   * @param {GetViewInput} [opts] - `opts` is an optional parameter of type `GetViewInput`. It is an
   * object that contains query string parameters that can be passed to the API endpoint. The function
   * loops through the `opts` object and adds each key-value pair as a query string parameter to the API
   * request. If `opts
   * @returns The function `getView` returns a Promise that resolves to a `PagedList` object.
   */
  const getView = async (
    msProvider: string,
    entity: EntityModel,
    viewName: string,
    opts?: GetViewInput,
  ): Promise<PagedList> => {
    const resultEntities: Entity[] = []
    // create query string params if passed
    const qps: Record<string, string> = {}
    if (opts) {
      Object.entries(opts).forEach(([key, value]) => {
        if (key === 'filters' || key === 'extraConditions') {
          qps[key] = value ? base64encode(JSON.stringify(value)) : ''
        } else if (value) {
          qps[key] = value
        }
      })
    }
    qps['includeAcl'] = 'true'
    // fetch
    const res = await c.read<PagedList>(`${msProvider}/api/Entity/${entity.name}/View/${viewName}`, qps)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      res.source.forEach((e) => {
        resultEntities.push(mapViewRow(e, entity, GetViewModel(msProvider, entity, viewName), msProvider))
      })
      res.source = resultEntities
      return res
    }
  }

  function GetViewModel(msProvider: string, entity: EntityModel, viewName: string): ViewModel {
    let viewModel = entity.getView(viewName) as ViewModel
    /*
     * If the entity model does not contain the view, it could be an external reference view
     * load the full model and search for an external reference coming from the specified ms provider
     */
    if (!viewModel) {
      const fullModel = readFullModel()
      const entityModel = fullModel.getExternalEntity(entity.name, msProvider)
      viewModel = entityModel?.getView(viewName) as ViewModel
    }
    return viewModel
  }

  /**
   * This retrieves the number of elements that the view has
   */
  const getViewCount = async (msProvider: string, entityName: string, viewName: string): Promise<CountResult> => {
    const res = await c.read<CountResult>(`${msProvider}/api/Entity/${entityName}/ViewCount/${viewName}`)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  /**
   * The function `getLookup` is an asynchronous function that retrieves a lookup value from a specified
   * API endpoint and maps it to a `LookupValue` object.
   * @param {string} msProvider - The `msProvider` parameter is a string that represents the provider for
   * the microservice. It is used to construct the API endpoint URL.
   * @param {string} entityName - The `entityName` parameter represents the name of the entity for which
   * you want to retrieve a lookup value.
   * @param {string} id - The `id` parameter is a string that represents the unique identifier of the
   * entity for which you want to retrieve the lookup value.
   * @param {LookupFieldDefinition} lookupDefinition - The `lookupDefinition` parameter is an object that
   * defines the structure of the lookup field. It contains information such as the field name, data
   * type, and any additional properties specific to the lookup field.
   * @param {LinkDefinition} link - The `link` parameter is an object that contains the following
   * properties:
   * @returns The function `getLookup` returns a Promise that resolves to a `LookupValue` object.
   */
  const getLookup = async (
    msProvider: string,
    entityName: string,
    id: string,
    lookupDefinition: LookupFieldDefinition,
    link: LinkDefinition,
  ): Promise<LookupValue> => {
    const qps: Record<string, string> = {}
    qps['linkedFields'] = link.linkedFields
    qps['include'] = link.include
    qps['guidValue'] = id
    qps['entityValue'] = entityName
    const res = await c.read<LookupValue>(`${msProvider}/api/Entity/GetLookupValue`, qps)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      const lookupValue = new LookupValue()
      lookupValue.mapField(res, lookupDefinition, msProvider)
      return lookupValue
    }
  }

  /**
   * This TypeScript function retrieves a Blob attachment from a given URL using an API call.
   * @param {string} url - The `url` parameter is a string representing the URL of the attachment that
   * needs to be retrieved. The function uses this URL to make a request to the server and fetch the
   * attachment data.
   * @returns The function `getAttachmentByUrl` returns a Promise that resolves to a Blob object.
   */
  const getAttachmentByUrl = async (url: string): Promise<Blob> => {
    const res = await c.readBlob<Blob>(`${url}`)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      return res
    }
  }

  const deleteEntity = async (
    msProvider: string,
    entityName: string,
    id: string,
    formName?: string,
  ): Promise<EntityOperation> => {
    const qs = new URLSearchParams()
    if (formName) {
      qs.append('formName', formName)
    }

    const parts = [`${msProvider}/api/Entity/${entityName}/${id}`]

    if (qs.size > 0) {
      parts.push(qs.toString())
    }

    const url = parts.join('?')
    const res = await c.delete<EntityOperation>(url)

    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      return res
    }
  }

  const deleteEntities = async (msProvider: string, entities: Record<string, string[]>): Promise<ServerOperation> => {
    const url = `${msProvider}/api/Entity`
    const res = await c.delete<ServerOperation, Record<string, string[]>>(url, entities)

    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  /**
   * Clones an entity.
   * @param msProvider The microservice provider.
   * @param entityName The name of the entity to clone.
   * @param id The ID of the entity to clone.
   * @param save Whether to save the cloned entity immediately.
   * @returns A promise that resolves to an EntityOperation object.
   */
  const cloneEntity = async (msProvider: string, entityName: string, id: string, save?: boolean): Promise<Entity> => {
    let url = `${msProvider}/api/Entity/Clone/${entityName}/${id}`

    if (save !== undefined) {
      url += `/${save}`
    }

    const res = await c.read<Entity>(url)

    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  return {
    createEntity,
    getEntity,
    getEntities,
    getView,
    getViewCount,
    saveEntities,
    forceSaveEntities,
    getLookup,
    getAttachmentByUrl,
    getSingleton,
    createEntityWithPredefinedData,
    deleteEntity,
    deleteEntities,
    cloneEntity,
  }
}
